import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Panel, PanelHeader, PanelHeaderBack, Group, Div, Text, Card, Headline, Button,
  Cell, IconButton, Alert, FormItem, Input, Select, Spinner, Snackbar,
  ModalRoot, ModalPageHeader,
} from '@vkontakte/vkui';
import {
  Icon24Write, Icon24Cancel, Icon28CalendarOutline, Icon28DeleteOutline,
  Icon28CheckCircleOutline,
} from '@vkontakte/icons';
import { useWeddingStore } from '../../stores/wedding';
import { useAuthStore } from '../../stores/auth';
import { notifyPermissionDenied } from '../../api/events';
import { resetOnboarding } from '../Onboarding/OnboardingWizard';
import { requestInstall, isStandalone, type InstallOutcome } from '../../utils/install';
import { canCreateWedding } from '../../utils/limits';
import type { Wedding } from '../../types';
import { DragHandle } from '../../ui/DragHandle';
import { ResizableModalPage, sheetId } from '../../ui/ResizableModalPage';

export default function SettingsPage() {
  const navigate = useNavigate();
  const {
    weddings, currentWedding, members, loadMembers, loadWeddings, selectWedding, updateWedding, deleteWedding, createWedding, leaveWedding,
  } = useWeddingStore();
  const { user, logout } = useAuthStore();

  const [loading, setLoading] = useState(false);
  const [editWedding, setEditWedding] = useState<Wedding | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Wedding | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [leaveTarget, setLeaveTarget] = useState<Wedding | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [snack, setSnack] = useState<{ text: string; ok: boolean } | null>(null);
  const [openToken, setOpenToken] = useState(0);
  const [installed, setInstalled] = useState(() => isStandalone());
  const [hint, setHint] = useState<string | null>(null);
  const [showManual, setShowManual] = useState(false);
  const SITE_URL = 'https://app.nevessta.ru';
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    wedding_date: '',
    venue: '',
    budget: '',
    currency: 'RUB',
  });
  const [editForm, setEditForm] = useState({
    name: '',
    partner_name: '',
    wedding_date: '',
    venue: '',
    budget: '',
    currency: 'RUB',
  });

  useEffect(() => {
    setLoading(true);
    loadWeddings().finally(() => setLoading(false));
  }, [loadWeddings]);

  useEffect(() => {
    setInstalled(isStandalone());
  }, []);

  function hintFor(kind: InstallOutcome['kind']): string | null {
    switch (kind) {
      case 'already':
        return 'Colibri уже на рабочем столе.';
      case 'ios':
        return 'Нажмите «Поделиться» внизу Safari → «На экран Домой».';
      case 'android':
        return 'Откройте меню браузера ⋮ → «Установить приложение» или «На главный экран».';
      case 'desktop':
        return 'В адресной строке Chrome нажмите значок установки или меню → «Установить Colibri».';
      case 'system':
      default:
        return null;
    }
  }

  async function handleInstall() {
    setHint(null);
    try {
      const result = await requestInstall();
      setInstalled(isStandalone());
      if (result.kind === 'system') {
        // native dialog is the feedback; if user dismissed, still ok
        setHint(null);
        return;
      }
      setHint(hintFor(result.kind));
    } catch (e) {
      console.error('install', e);
      setHint('Не получилось автоматически. Добавьте ярлык через меню браузера.');
    }
  }

  async function openEdit(w: Wedding) {
    if (w.owner_id !== user?.id) {
      let me = members.find((m) => m.user_id === user?.id);
      if (!me) {
        try {
          await loadMembers();
          me = useWeddingStore.getState().members.find((m) => m.user_id === user?.id);
        } catch {
          me = undefined;
        }
      }
      const level = me?.permissions?.find((p) => p.module === 'settings')?.level;
      if (me && level !== 'write') {
        notifyPermissionDenied();
        return;
      }
    }
    setOpenToken((t) => t + 1);
    setEditWedding(w);
    setEditForm({
      name: w.name || '',
      partner_name: w.partner_name || '',
      wedding_date: w.wedding_date ? String(w.wedding_date).slice(0, 10) : '',
      venue: w.venue || '',
      budget: w.budget != null ? String(w.budget) : '',
      currency: w.currency || 'RUB',
    });
  }

  async function handleDelete() {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await deleteWedding(deleteTarget.id);
      setSnack({ text: `Свадьба «${deleteTarget.name}» удалена`, ok: true });
      setDeleteTarget(null);
    } catch (e) {
      console.error(e);
      setSnack({ text: 'Не удалось удалить свадьбу', ok: false });
    } finally {
      setDeleting(false);
    }
  }

  async function handleLeave() {
    if (!leaveTarget || leaving) return;
    setLeaving(true);
    try {
      await leaveWedding(leaveTarget.id);
      setSnack({ text: `Вы вышли из свадьбы «${leaveTarget.name}»`, ok: true });
      setLeaveTarget(null);
    } catch (e) {
      console.error(e);
      setSnack({ text: 'Не удалось выйти из свадьбы', ok: false });
    } finally {
      setLeaving(false);
    }
  }

  return (
    <Panel>
      <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/')} />}>
        Настройки
      </PanelHeader>

      {/* Profile */}
      <Group header={<Text>Профиль</Text>}>
        <Card style={{ margin: 12, padding: 14 }}>
          <Headline level="2">{user?.first_name || 'Гость'} {user?.last_name || ''}</Headline>
          {user?.vk_id != null && (
            <Text style={{ marginTop: 4, color: 'var(--vkui--color_text_secondary)', fontSize: 13 }}>
              VK ID: {user.vk_id}
            </Text>
          )}
        </Card>
      </Group>

      {/* Current wedding */}
      {currentWedding && (
        <Group header={<Text>Текущая свадьба</Text>}>
          <Card style={{ margin: 12, padding: 14 }}>
            <Headline level="2">{currentWedding.name}</Headline>
            {currentWedding.wedding_date && (
              <Text style={{ marginTop: 4 }}>
                Дата: {new Date(currentWedding.wedding_date).toLocaleDateString('ru-RU')}
              </Text>
            )}
            {currentWedding.venue && (
              <Text style={{ marginTop: 2, color: 'var(--vkui--color_text_secondary)' }}>
                {currentWedding.venue}
              </Text>
            )}
            <Text style={{ marginTop: 8, fontSize: 13, color: 'var(--vkui--color_text_secondary)' }}>
              Код приглашения: <strong>{currentWedding.invite_code}</strong>
            </Text>
            <Button
              style={{ marginTop: 10 }}
              size="m"
              mode="secondary"
              onClick={() => navigate('/members')}
            >
              Участники и права доступа
            </Button>
            <Button
              style={{ marginTop: 8 }}
              size="m"
              mode="secondary"
              onClick={() => void openEdit(currentWedding)}
            >
              Изменить свадьбу
            </Button>
          </Card>
        </Group>
      )}

      {/* Weddings list (moved from Dashboard «Свадьбы») */}
      <Group header={<Text>Мои свадьбы</Text>}>
        {loading ? (
          <Div style={{ textAlign: 'center', padding: 24 }}><Spinner size="m" /></Div>
        ) : (
          <>
            {weddings.length === 0 && (
              <Div style={{ textAlign: 'center', padding: 24 }}>
                <Text>Пока нет свадеб</Text>
              </Div>
            )}
            {weddings.map((w) => (
              <Cell
                key={w.id}
                before={<Icon28CalendarOutline />}
                onClick={() => {
                  selectWedding(w.id).catch(() => {});
                  setSnack({ text: `Выбрана: ${w.name}`, ok: true });
                }}
                after={
                  w.owner_id === user?.id ? (
                    <>
                      <IconButton
                        label="Редактировать"
                        onClick={(e) => {
                          e.stopPropagation();
                          void openEdit(w);
                        }}
                      >
                        <Icon24Write />
                      </IconButton>
                      <IconButton
                        label="Удалить"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(w);
                        }}
                      >
                        <Icon28DeleteOutline fill="var(--colibri-danger)" />
                      </IconButton>
                    </>
                  ) : (
                    <Button
                      size="s"
                      mode="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLeaveTarget(w);
                      }}
                    >
                      Выйти
                    </Button>
                  )
                }
              >
                <Text>{w.name}</Text>
                {w.wedding_date && (
                  <Text style={{ color: 'var(--vkui--color_text_secondary)', fontSize: 12 }}>
                    {new Date(w.wedding_date).toLocaleDateString('ru-RU')}
                    {currentWedding?.id === w.id ? ' · текущая' : ''}
                  </Text>
                )}
              </Cell>
            ))}
            <Div>
              <Button
                size="m"
                mode="primary"
                stretched
                onClick={() => {
                  setCreateForm({ name: '', wedding_date: '', venue: '', budget: '', currency: 'RUB' });
                  setOpenToken((t) => t + 1);
                  setShowCreate(true);
                }}
              >
                Добавить свадьбу
              </Button>
            </Div>
          </>
        )}
      </Group>

      {/* Home screen shortcut */}
      <Group header={<Text>Ярлык на рабочий стол</Text>}>
        <Card style={{ margin: 12, padding: 14 }}>
          <Headline level="2">Добавить Colibri на экран</Headline>
          <Text style={{ marginTop: 6, fontSize: 13, color: 'var(--vkui--color_text_secondary)' }}>
            Значок появится рядом с приложениями — вход в один тап.
          </Text>

          {!installed && (
            <Button
              style={{ marginTop: 12 }}
              size="m"
              mode="primary"
              stretched
              onClick={() => {
                void handleInstall();
              }}
            >
              Добавить ярлык
            </Button>
          )}
          {installed && (
            <Text style={{ marginTop: 12, fontSize: 14, color: 'var(--colibri-success)' }}>
              ✓ Colibri уже есть на рабочем столе
            </Text>
          )}
          {hint && (
            <Text style={{ marginTop: 10, fontSize: 14, lineHeight: 1.45 }}>
              {hint}
            </Text>
          )}

          <div style={{
            marginTop: 14, padding: '12px 14px', borderRadius: 12,
            background: 'var(--vkui--color_background_secondary)',
          }}>
            <div style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)', marginBottom: 6 }}>
              Ссылка на сайт (для ярлыка откройте в браузере):
            </div>
            <a
              href={SITE_URL}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: 15, fontWeight: 600, color: 'var(--colibri-primary)', wordBreak: 'break-all' }}
            >
              {SITE_URL}
            </a>
            <Button
              size="s"
              mode="secondary"
              style={{ marginTop: 10 }}
              onClick={() => {
                void navigator.clipboard?.writeText(SITE_URL).then(() => setHint('Ссылка скопирована')).catch(() => setHint(SITE_URL));
              }}
            >
              Скопировать ссылку
            </Button>
          </div>

          {/* Manual guide — collapsed by default */}
          <button
            type="button"
            onClick={() => setShowManual(!showManual)}
            style={{
              width: '100%',
              marginTop: 12,
              padding: '12px 14px',
              borderRadius: 12,
              border: '1px solid var(--colibri-border)',
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: 14,
              fontWeight: 600,
              textAlign: 'left',
            }}
          >
            <span>Инструкция</span>
            <span style={{ fontSize: 18, opacity: 0.6, transform: showManual ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>
              ›
            </span>
          </button>

          {showManual && (
            <div style={{
              marginTop: 8,
              padding: '12px 14px',
              borderRadius: 12,
              background: 'var(--vkui--color_background_secondary)',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 14 }}>📱 iPhone / iPad</div>
                <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.55 }}>
                  <li>Откройте в <strong>Safari</strong>: <strong style={{ wordBreak: 'break-all' }}>{SITE_URL}</strong></li>
                  <li>Внизу <strong>«Поделиться»</strong> ↑</li>
                  <li><strong>«На экран „Домой“»</strong> → <strong>«Добавить»</strong></li>
                </ol>
              </div>

              <div>
                <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 14 }}>🤖 Android</div>
                <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.55 }}>
                  <li>Откройте в <strong>Chrome</strong>: <strong style={{ wordBreak: 'break-all' }}>{SITE_URL}</strong></li>
                  <li>Меню <strong>⋮</strong> → <strong>«Установить приложение»</strong></li>
                  <li><strong>«Установить»</strong></li>
                </ol>
              </div>

              <div>
                <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 14 }}>💻 Компьютер</div>
                <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.55 }}>
                  <li>Откройте <strong style={{ wordBreak: 'break-all' }}>{SITE_URL}</strong></li>
                  <li>Значок установки в адресной строке</li>
                  <li><strong>«Установить Colibri…»</strong></li>
                </ol>
              </div>

              <div>
                <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 14 }}>💬 Внутри VK</div>
                <Text style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--vkui--color_text_secondary)' }}>
                  VK не даёт ярлык на рабочий стол. Можно <strong>«⋯» → «Добавить в меню»</strong>.
                  Для ярлыка откройте <strong style={{ wordBreak: 'break-all' }}>{SITE_URL}</strong> в браузере.
                </Text>
              </div>
            </div>
          )}
        </Card>
      </Group>

      {/* App */}
      <Group>
        <Div>
          <Button
            size="m"
            mode="secondary"
            stretched
            style={{ marginBottom: 12 }}
            onClick={() => {
              resetOnboarding();
              navigate('/');
              window.location.reload();
            }}
          >
            Пройти онбординг заново
          </Button>
          <Text
            style={{ color: 'var(--vkui--color_text_negative)', cursor: 'pointer' }}
            onClick={() => {
              logout();
              navigate('/auth', { replace: true });
            }}
          >
            Выйти
          </Text>
        </Div>
      </Group>

      {/* Create modal */}
      <ModalRoot
        activeModal={
          showCreate
            ? sheetId('settings-wedding-create', openToken)
            : editWedding
              ? sheetId('settings-wedding-edit', openToken)
              : null
        }
        onClose={() => {
          setShowCreate(false);
          setEditWedding(null);
        }}
      >
        <ResizableModalPage
          openToken={openToken}
          id="settings-wedding-create"
          onClose={() => setShowCreate(false)}
          settlingHeight={80}
        >
          <ModalPageHeader>Новая свадьба</ModalPageHeader>
          <DragHandle />
          <Group>
            <FormItem top="Название *">
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Свадьба Анны и Дмитрия"
              />
            </FormItem>
            <FormItem top="Дата свадьбы">
              <Input
                type="date"
                value={createForm.wedding_date}
                onChange={(e) => setCreateForm({ ...createForm, wedding_date: e.target.value })}
              />
            </FormItem>
            <FormItem top="Место">
              <Input
                value={createForm.venue}
                onChange={(e) => setCreateForm({ ...createForm, venue: e.target.value })}
                placeholder="Ресторан / адрес"
              />
            </FormItem>
            <FormItem top="Бюджет">
              <Input
                type="number"
                inputMode="decimal"
                value={createForm.budget}
                onChange={(e) => setCreateForm({ ...createForm, budget: e.target.value })}
                placeholder="Например: 500000"
              />
            </FormItem>
            <FormItem top="Валюта">
              <Select
                value={createForm.currency}
                onChange={(e) => setCreateForm({ ...createForm, currency: e.target.value })}
                options={[
                  { label: 'RUB — ₽', value: 'RUB' },
                  { label: 'USD — $', value: 'USD' },
                  { label: 'EUR — €', value: 'EUR' },
                  { label: 'KZT — ₸', value: 'KZT' },
                  { label: 'BYN — Br', value: 'BYN' },
                ]}
              />
            </FormItem>
            <Div>
              <Button
                size="l"
                mode="primary"
                stretched
                disabled={creating || !createForm.name.trim()}
                onClick={async () => {
                  if (!createForm.name.trim() || creating) return;
                  if (!canCreateWedding(weddings.length)) {
                    setSnack({ text: 'Создание второй свадьбы будет доступно по подписке', ok: false });
                    return;
                  }
                  setCreating(true);
                  try {
                    const w = await createWedding({
                      name: createForm.name.trim(),
                      wedding_date: createForm.wedding_date || undefined,
                      venue: createForm.venue.trim() || undefined,
                      budget: createForm.budget ? Number(createForm.budget) : undefined,
                      currency: createForm.currency,
                    });
                    await loadWeddings();
                    await selectWedding(w.id).catch(() => {});
                    setOpenToken((t) => t + 1);
                    setShowCreate(false);
                    setSnack({ text: `Свадьба «${w.name}» создана`, ok: true });
                  } catch {
                    setSnack({ text: 'Не удалось создать свадьбу', ok: false });
                  } finally {
                    setCreating(false);
                  }
                }}
              >
                {creating ? 'Создание…' : 'Создать'}
              </Button>
              <Button size="l" mode="secondary" stretched style={{ marginTop: 8 }} onClick={() => setShowCreate(false)}>
                Отмена
              </Button>
            </Div>
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="settings-wedding-edit" onClose={() => setEditWedding(null)} settlingHeight={80}>
          <ModalPageHeader>Редактировать свадьбу</ModalPageHeader>
          <DragHandle />
          <Group>
            <FormItem top="Название *">
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Свадьба Анны и Дмитрия"
              />
            </FormItem>
            <FormItem top="Партнёр">
              <Input
                value={editForm.partner_name}
                onChange={(e) => setEditForm({ ...editForm, partner_name: e.target.value })}
              />
            </FormItem>
            <FormItem top="Дата свадьбы">
              <Input
                type="date"
                value={editForm.wedding_date}
                onChange={(e) => setEditForm({ ...editForm, wedding_date: e.target.value })}
              />
            </FormItem>
            <FormItem top="Место">
              <Input
                value={editForm.venue}
                onChange={(e) => setEditForm({ ...editForm, venue: e.target.value })}
              />
            </FormItem>
            <FormItem top="Бюджет">
              <Input
                type="number"
                inputMode="decimal"
                value={editForm.budget}
                onChange={(e) => setEditForm({ ...editForm, budget: e.target.value })}
              />
            </FormItem>
            <FormItem top="Валюта">
              <Select
                value={editForm.currency}
                onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                options={[
                  { label: 'RUB — ₽', value: 'RUB' },
                  { label: 'USD — $', value: 'USD' },
                  { label: 'EUR — €', value: 'EUR' },
                  { label: 'KZT — ₸', value: 'KZT' },
                  { label: 'BYN — Br', value: 'BYN' },
                ]}
              />
            </FormItem>
            <Div>
              <Button
                size="l"
                mode="primary"
                stretched
                disabled={editSaving || !editForm.name.trim()}
                onClick={async () => {
                  if (!editWedding) return;
                  setEditSaving(true);
                  try {
                    await updateWedding(editWedding.id, {
                      name: editForm.name.trim(),
                      partner_name: editForm.partner_name.trim() || undefined,
                      wedding_date: editForm.wedding_date || undefined,
                      venue: editForm.venue.trim() || undefined,
                      budget: editForm.budget ? Number(editForm.budget) : undefined,
                      currency: editForm.currency,
                    });
                    await loadWeddings();
                    setOpenToken((t) => t + 1);
                    setEditWedding(null);
                    setSnack({ text: 'Свадьба сохранена', ok: true });
                  } catch {
                    setSnack({ text: 'Не удалось сохранить', ok: false });
                  } finally {
                    setEditSaving(false);
                  }
                }}
              >
                {editSaving ? 'Сохранение…' : 'Сохранить'}
              </Button>
              <Button size="l" mode="secondary" stretched style={{ marginTop: 8 }} onClick={() => setEditWedding(null)}>
                Отмена
              </Button>
            </Div>
          </Group>
        </ResizableModalPage>
      </ModalRoot>

      {/* Delete confirm */}
      {deleteTarget && (<Alert
        actionsLayout="vertical"
        actions={[
          {
            title: deleting ? 'Удаление…' : 'Удалить свадьбу',
            mode: 'destructive',
            // VKUI defers action until exit animation; onClose unmounts Alert first.
            // Run immediately so delete actually happens.
            autoCloseDisabled: true,
            action: (args) => {
              void handleDelete().finally(() => args?.close?.());
            },
          },
          {
            title: 'Отмена',
            mode: 'cancel',
            },
        ]}
        title="Удалить свадьбу?"
        description={
          deleteTarget
            ? `«${deleteTarget.name}» будет удалена со всеми данными: гости, задачи, бюджет. Действие необратимо.`
            : ''
        }
        onClose={() => setDeleteTarget(null)}
        onClosed={() => setDeleteTarget(null)}
      />
      )}

      {/* Leave confirm */}
      {leaveTarget && (<Alert
        actionsLayout="vertical"
        actions={[
          {
            title: leaving ? 'Выходим…' : 'Выйти из свадьбы',
            mode: 'destructive',
            autoCloseDisabled: true,
            action: (args) => {
              void handleLeave().finally(() => args?.close?.());
            },
          },
          {
            title: 'Отмена',
            mode: 'cancel',
          },
        ]}
        title="Выйти из свадьбы?"
        description={`Вы перестанете видеть свадьбу «${leaveTarget.name}» и её данные. Организатор сможет пригласить вас снова.`}
        onClose={() => setLeaveTarget(null)}
        onClosed={() => setLeaveTarget(null)}
      />
      )}

      {snack && (
        <Snackbar
          onClose={() => setSnack(null)}
          onClosed={() => setSnack(null)}
          before={
            snack.ok
              ? <Icon28CheckCircleOutline fill="var(--colibri-success)" />
              : <Icon24Cancel fill="var(--colibri-danger)" />
          }
        >
          {snack.text}
        </Snackbar>
      )}
    </Panel>
  );
}
